import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { createVideo, generateScript, listVideoTemplates, listVoiceProfiles } from '../../services/contentAiService';

export default function ContentPage() {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('');
  const [minutes, setMinutes] = useState(3);
  const [style, setStyle] = useState('educativo, profissional, direto');
  const [scriptText, setScriptText] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [generateVoice, setGenerateVoice] = useState(true);
  const [generateImages, setGenerateImages] = useState(true);
  const [imagesCount, setImagesCount] = useState(6);
  const [scheduledPublishAt, setScheduledPublishAt] = useState('');
  const [voiceProfiles, setVoiceProfiles] = useState([]);
  const [voiceProfileId, setVoiceProfileId] = useState('');
  const [voiceSpeed, setVoiceSpeed] = useState(1);
  const [voiceEmotion, setVoiceEmotion] = useState(0.5);

  const selectedTemplate = useMemo(() => {
    const id = Number(templateId);
    if (!id) return null;
    return (templates || []).find(t => Number(t.id) === id) || null;
  }, [templateId, templates]);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await listVideoTemplates();
        const data = resp?.data || [];
        setTemplates(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(e?.message || 'Falha ao carregar templates');
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadVoices = async () => {
      try {
        const resp = await listVoiceProfiles();
        const data = resp?.data || [];
        setVoiceProfiles(Array.isArray(data) ? data : []);
      } catch (_) {}
    };
    loadVoices();
  }, []);

  const handleGenerateScript = async () => {
    setLoading(true);
    try {
      const resp = await generateScript({ topic, audience, minutes, style });
      const out = resp?.data?.script || '';
      setScriptText(String(out || ''));
      toast.success('Roteiro gerado');
    } catch (e) {
      toast.error(e?.message || 'Falha ao gerar roteiro');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVideo = async () => {
    if (!topic.trim() && !scriptText.trim()) {
      toast.error('Informe topic ou scriptText');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        topic: topic.trim() || undefined,
        audience: audience.trim() || undefined,
        minutes: Number(minutes) || undefined,
        style: style.trim() || undefined,
        scriptText: scriptText.trim() || undefined,
        generateScript: !scriptText.trim(),
        templateId: selectedTemplate ? Number(selectedTemplate.id) : undefined,
        generateVoice,
        generateImages,
        imagesCount: Number(imagesCount) || 6,
        scheduledPublishAt: scheduledPublishAt ? new Date(scheduledPublishAt).toISOString() : null,
        voiceProfileId: voiceProfileId ? Number(voiceProfileId) : undefined,
        voiceOverrides: voiceProfileId ? { speed: Number(voiceSpeed) || 1, emotion: Number(voiceEmotion) || 0.5 } : undefined
      };
      const resp = await createVideo(payload);
      toast.success(`Vídeo criado (ID ${resp?.data?.videoId || ''})`);
      setScriptText('');
    } catch (e) {
      toast.error(e?.message || 'Falha ao criar vídeo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={2} display="grid" gap={2}>
      <Typography variant="h5">Conteúdo (IA)</Typography>

      <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
        <TextField label="Tema" value={topic} onChange={(e) => setTopic(e.target.value)} fullWidth />
        <TextField label="Público" value={audience} onChange={(e) => setAudience(e.target.value)} fullWidth />
        <TextField
          label="Duração (min)"
          type="number"
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          fullWidth
          inputProps={{ min: 1, max: 30 }}
        />
        <TextField label="Estilo" value={style} onChange={(e) => setStyle(e.target.value)} fullWidth />
        <FormControl fullWidth>
          <InputLabel>Template</InputLabel>
          <Select label="Template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <MenuItem value="">(Sem template)</MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Agendar publicação (opcional)"
          type="datetime-local"
          value={scheduledPublishAt}
          onChange={(e) => setScheduledPublishAt(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1}>
          <Switch checked={generateVoice} onChange={(e) => setGenerateVoice(e.target.checked)} />
          <Typography>Narração IA</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Switch checked={generateImages} onChange={(e) => setGenerateImages(e.target.checked)} />
          <Typography>Imagens IA</Typography>
        </Box>
        <TextField
          label="Qtd imagens"
          type="number"
          value={imagesCount}
          onChange={(e) => setImagesCount(e.target.value)}
          disabled={!generateImages}
          inputProps={{ min: 1, max: 20 }}
          sx={{ width: 140 }}
        />
      </Box>

      {generateVoice ? (
        <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '2fr 1fr 1fr' }}>
          <FormControl fullWidth>
            <InputLabel>Voz (opcional)</InputLabel>
            <Select label="Voz (opcional)" value={voiceProfileId} onChange={(e) => setVoiceProfileId(e.target.value)}>
              <MenuItem value="">Padrão</MenuItem>
              {voiceProfiles.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>{`#${p.id} - ${p.name} (${p.provider})`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Velocidade"
            type="number"
            value={voiceSpeed}
            onChange={(e) => setVoiceSpeed(e.target.value)}
            disabled={!voiceProfileId}
            inputProps={{ min: 0.5, max: 2, step: 0.05 }}
            fullWidth
          />
          <TextField
            label="Emoção"
            type="number"
            value={voiceEmotion}
            onChange={(e) => setVoiceEmotion(e.target.value)}
            disabled={!voiceProfileId}
            inputProps={{ min: 0, max: 1, step: 0.05 }}
            fullWidth
          />
        </Box>
      ) : null}

      <TextField
        label="Roteiro"
        value={scriptText}
        onChange={(e) => setScriptText(e.target.value)}
        fullWidth
        multiline
        minRows={10}
      />

      <Box display="flex" gap={2} flexWrap="wrap">
        <Button variant="outlined" disabled={loading} onClick={handleGenerateScript}>
          Gerar roteiro
        </Button>
        <Button variant="contained" disabled={loading} onClick={handleCreateVideo}>
          Criar vídeo e renderizar
        </Button>
      </Box>
    </Box>
  );
}
